import { animated, useSpring } from "@react-spring/three";
import { Center, Text3D } from "@react-three/drei";
import React, { useMemo } from "react";

// Move StatText outside StatsBar to prevent recreation on every render
const StatText = React.memo(
	({
		position,
		label,
		percentage,
		barColor,
		textColor,
		show,
		staggerDelay = 0,
	}) => {
		// Track the animated number for display
		const [displayNumber, setDisplayNumber] = React.useState(0);

		// Animation sequence:
		// 1. Shadow fades in left-to-right + fills top-to-bottom
		// 2. Text pops up from top
		// 3. Number counts up as bar grows left-to-right

		// Shadow animation: fade + scale from left-to-right + top-to-bottom
		const shadowSpring = useSpring({
			from: {
				scaleX: 0,
				scaleY: 0,
				opacity: 0,
			},
			to: {
				scaleX: show ? 1 : 0,
				scaleY: show ? 1 : 0,
				opacity: show ? 0.3 : 0,
			},
			config: { tension: 120, friction: 20 },
			delay: staggerDelay,
		});

		// Bar fill animation: grows left-to-right (delayed after shadow starts)
		const barSpring = useSpring({
			from: {
				progress: 0,
				opacity: 0,
			},
			to: {
				progress: show ? percentage : 0,
				opacity: show ? 1 : 0,
			},
			config: { tension: 80, friction: 20 },
			delay: staggerDelay + 500,
			onChange: (result) => {
				setDisplayNumber(Math.round(result.value.progress));
			},
		});

		// Text animation: rise from bar upward (delayed after bar starts growing)
		const textSpring = useSpring({
			from: {
				offsetY: -0.5,
				opacity: 0,
				scale: 1,
			},
			to: {
				offsetY: show ? 0 : -0.5,
				opacity: show ? 1 : 0,
				scale: 1,
			},
			config: { tension: 200, friction: 30 },
			delay: staggerDelay + 300,
		});

		return (
			<group
				position-x={position[0]}
				position-y={position[1]}
				position-z={position[2]}
			>
				{/* Label - pops from top */}
				<animated.group
					position-y={textSpring.offsetY}
					scale={textSpring.scale.to((s) => [s, s, s])}
				>
					<Center position={[-1.55, -0.5, -0.1]}>
						<Text3D
							font="/fonts/helvetiker_bold.typeface.json"
							size={0.6}
							height={0.1}
							curveSegments={12}
						>
							{label}
							<animated.meshBasicMaterial
								color={textColor}
								transparent
								opacity={textSpring.opacity}
							/>
						</Text3D>
					</Center>
				</animated.group>

				{/* Percentage - pops from top with counting animation */}
				<animated.group
					position-y={textSpring.offsetY}
					scale={textSpring.scale.to((s) => [s, s, s])}
				>
					<Center position={[1.1, -0.5, -0.1]}>
						<Text3D
							font="/fonts/helvetiker_bold.typeface.json"
							size={0.7}
							height={0.1}
							curveSegments={12}
						>
							{displayNumber}%
							<animated.meshStandardMaterial
								color={textColor}
								emissive={textColor}
								emissiveIntensity={0.3}
								transparent
								opacity={textSpring.opacity}
							/>
						</Text3D>
					</Center>
				</animated.group>

				{/* Progress bar shadow - fades in left-to-right + top-to-bottom */}
				<animated.mesh
					position-x={shadowSpring.scaleX.to((s) => -2 + (4 * s) / 2)}
					position-y={-1.2}
					position-z={-0.03}
					scale-x={shadowSpring.scaleX}
				>
					<planeGeometry args={[4, 0.3]} />
					<animated.meshBasicMaterial
						color={barColor}
						transparent
						opacity={shadowSpring.opacity}
					/>
				</animated.mesh>

				{/* Progress bar fill - grows left-to-right */}
				<animated.mesh
					position-x={barSpring.progress.to((p) => -2 + (4 * p) / 100 / 2)}
					position-y={-1.2}
					position-z={-0.01}
					scale-x={barSpring.progress.to((p) => p / 100)}
				>
					<planeGeometry args={[4, 0.3]} />
					<animated.meshBasicMaterial
						color={barColor}
						transparent
						opacity={barSpring.opacity}
					/>
				</animated.mesh>
			</group>
		);
	},
);

export const StatsBar = React.memo(({ shots, show }) => {
	// Memoize positions to prevent StatText from seeing new array instances on every render
	const positions = useMemo(
		() => ({
			FG: [-4.5, 4, -7],
			TP_2P: [-4.5, 6, -7],
			TP_3P: [4.5, 6, -7],
			FT: [4.5, 4, -7],
		}),
		[],
	);

	// Generate random stagger delays for each stat (200-700ms range for noticeable stagger)
	const staggerDelays = useMemo(
		() => ({
			FG: Math.random() * 500 + 200,
			TP_2P: Math.random() * 500 + 200,
			TP_3P: Math.random() * 500 + 200,
			FT: Math.random() * 500 + 200,
		}),
		[],
	);

	// Calculate shooting statistics
	const stats = useMemo(() => {
		if (!shots || shots.length === 0) {
			return {
				FGPercentage: 0,
				FGMade: 0,
				FGTotal: 0,
				TPPercentage: 0,
				TPMade: 0,
				TPTotal: 0,
				FTPercentage: 0,
				FTMade: 0,
				FTTotal: 0,
			};
		}

		const FGMade = shots.filter(
			(shot) =>
				shot.field_goal_position === "2PFG" &&
				shot.field_goal_result === "score",
		).length;
		const FGTotal = shots.filter(
			(shot) => shot.field_goal_position === "2PFG",
		).length;

		const TPMade = shots.filter(
			(shot) =>
				shot.field_goal_position === "3PFG" &&
				shot.field_goal_result === "score",
		).length;
		const TPTotal = shots.filter(
			(shot) => shot.field_goal_position === "3PFG",
		).length;

		const FTMade = shots.filter(
			(shot) =>
				shot.field_goal_position === "FT" && shot.field_goal_result === "score",
		).length;
		const FTTotal = shots.filter(
			(shot) => shot.field_goal_position === "FT",
		).length;

		return {
			FGPercentage: FGTotal > 0 ? Math.round((FGMade / FGTotal) * 100) : 0,
			FGMade,
			FGTotal,
			TPPercentage: TPTotal > 0 ? Math.round((TPMade / TPTotal) * 100) : 0,
			TPMade,
			TPTotal,
			FTPercentage: FTTotal > 0 ? Math.round((FTMade / FTTotal) * 100) : 0,
			FTMade,
			FTTotal,
		};
	}, [shots]);

	return (
		<group>
			{/* Position stats in a horizontal line at the top, near the basket */}
			{/* Camera is at [0, 15, 20] looking down at the court */}

			{/* FG stat - left side */}
			<StatText
				position={positions.FG}
				label="FG"
				percentage={stats.FGPercentage}
				barColor="#6B7BE8"
				textColor="#FFFFFF"
				show={show}
				staggerDelay={staggerDelays.FG}
			/>

			{/* 2P stat - center left */}
			<StatText
				position={positions.TP_2P}
				label="2P"
				percentage={stats.FGPercentage}
				barColor="#6B7BE8"
				textColor="#FFFFFF"
				show={show}
				staggerDelay={staggerDelays.TP_2P}
			/>

			{/* 3P stat - center right */}
			<StatText
				position={positions.TP_3P}
				label="3P"
				percentage={stats.TPPercentage}
				barColor="#6B7BE8"
				textColor="#FFFFFF"
				show={show}
				staggerDelay={staggerDelays.TP_3P}
			/>

			{/* FT stat - right side */}
			<StatText
				position={positions.FT}
				label="FT"
				percentage={stats.FTPercentage}
				barColor="#6B7BE8"
				textColor="#FFFFFF"
				show={show}
				staggerDelay={staggerDelays.FT}
			/>
		</group>
	);
});
